import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Box, Button, Card, Center, Checkbox, Container, Divider, Group, Loader,
  Modal, ScrollArea, Stack, Text, Textarea, TextInput, ThemeIcon, Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconBan, IconCircleCheck, IconCircleX, IconClockOff, IconDownload,
  IconFileText, IconRefresh, IconWriting,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { PhoneInput } from '../components/PhoneInput';
import { SignaturePad } from '../components/SignaturePad';

// Backend /docs endpointi. Frontend (www.mysata.uz) va backend alohida domenlarda —
// shuning uchun to'liq API manzili orqali chaqiramiz.
const API_URL = import.meta.env.VITE_API_URL || 'https://sata.up.railway.app';

// MUHIM: bu sahifani ota-onalar ochiq havola orqali telefonda ochadi — matnlar
// admin panel tilidan qat'i nazar DOIMO o'zbekcha bo'lishi kerak (i18n'ga o'tkazilmaydi).

/**
 * Holat mashinasi (backend `lib/contractSign.ts` bilan bir xil):
 *   SENT → VIEWED → SIGNED | REJECTED; maktab istalgan paytda CANCELLED qiladi.
 * `EXPIRED` alohida status EMAS — `expiresAt` dan hisoblanadi (`isExpired`).
 *
 * DIQQAT: ochiq `/docs/:token/info` javobida `VIEWED` ataylab `SENT` deb ko'rsatiladi
 * (ota-ona uchun harakat bir xil — forma ochiq turadi), haqiqiy holat esa `statusRaw`
 * da keladi. Shuning uchun ikkalasini ham qo'llaymiz: backend xaritalashni olib
 * tashlasa ham sahifa buzilmaydi.
 */
type ContractStatus = 'SENT' | 'VIEWED' | 'SIGNED' | 'REJECTED' | 'CANCELLED';

/** Imzolash formasi ochiq turadigan holatlar. */
const PENDING: ContractStatus[] = ['SENT', 'VIEWED'];

interface ContractLine { text: string; bold?: boolean; center?: boolean }
interface ContractSection { title?: string; lines: ContractLine[] }

interface ContractInfo {
  status: ContractStatus;
  /** Haqiqiy (xaritalanmagan) holat — `VIEWED` faqat shu yerda ko'rinadi. */
  statusRaw?: ContractStatus;
  schoolName: string;
  summary: {
    contractNumber: string;
    date: string;
    studentFullName: string;
    monthlyPayment: number;
    monthsCount: number;
    fromDate: string;
    toDate: string;
  };
  sections: ContractSection[];
  prefill: { parentFullName: string; parentAddress: string; passport: string; jshshir: string; parentPhone: string };
  signerName: string | null;
  signedAt: string | null;
  rejectedAt: string | null;
  rejectReason: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
  viewedAt?: string | null;
  expiresAt?: string | null;
  isExpired?: boolean;
  /**
   * Imzo QO'YILGANMI. Imzo RASMI ochiq javobda ATAYLAB yo'q (PII: havolani qo'lga
   * kiritgan har kim imzo namunasini ko'rib qolardi) — shuning uchun rasm emas,
   * shu bayroq ko'rsatiladi.
   */
  hasSignature?: boolean;
  /**
   * Ochiq havola orqali qolgan yuklab olishlar soni. Semantikasi o'zgargan: hisoblagich
   * IMZOLANGANDAN keyin ham ishlaydi (imzolash paytida yangilanadi), ya'ni ".docx" ni
   * cheksiz yuklab bo'lmaydi. Ilova va maktab arxividagi nusxaga bu cheklov tegishli emas.
   */
  remainingUses: number;
}

type Load = 'loading' | 'ready' | 'notfound' | 'gone' | 'error';

const dmy = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU');
};
const dmyTime = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('ru-RU')} ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
};
const som = (n?: number) => `${(n ?? 0).toLocaleString('ru-RU').replace(/,/g, ' ')} so'm`;

/** Pasportni "AB 1234567" ko'rinishiga keltiradi. */
function formatPassport(v: string): string {
  const s = v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 9);
  const letters = s.slice(0, 2).replace(/[^A-Z]/g, '');
  const digits = s.slice(letters.length).replace(/\D/g, '').slice(0, 7);
  if (!letters) return digits;
  return digits ? `${letters} ${digits}` : letters;
}

/** Server matni bo'lmaganda (HTML sahifa, proksi xatosi) holatga mos zaxira xabar. */
function statusFallback(status: number): string {
  if (status === 404) return 'Bunday shartnoma havolasi topilmadi.';
  if (status === 409) return "Shartnoma holati o'zgargan — sahifa yangilanmoqda.";
  if (status === 410) return 'Havola endi ishlamaydi.';
  if (status === 413) return "Imzo rasmi juda katta — imzoni qaytadan, soddaroq qilib qo'ying.";
  if (status === 429) return "Juda ko'p urinish bo'ldi. Bir necha daqiqadan so'ng qayta urinib ko'ring.";
  return "Xatolik yuz berdi. Keyinroq urinib ko'ring.";
}

/** Server o'zbekcha xato matnini beradi; JSON bo'lmasa — holatga mos zaxira matn. */
async function apiError(res: Response, fallback?: string): Promise<string> {
  try {
    const j = (await res.json()) as { error?: string };
    if (j?.error) return j.error;
  } catch {
    /* JSON emas — masalan HTML xato sahifasi */
  }
  return fallback ?? statusFallback(res.status);
}

/**
 * OCHIQ shartnoma sahifasi (havola: www.mysata.uz/docs/:token).
 * Ota-ona shartnoma matnini o'qiydi, o'z ma'lumotlarini kiritadi, onlayn imzo
 * qo'yib tasdiqlaydi (yoki rad etadi), so'ng .docx nusxasini yuklab oladi.
 */
export default function ContractDownload() {
  const { token = '' } = useParams();
  const [load, setLoad] = useState<Load>('loading');
  const [info, setInfo] = useState<ContractInfo | null>(null);
  // Havola yopilgan (410) yoki umumiy xato bo'lganda — SERVER matni ko'rsatiladi
  const [blockMsg, setBlockMsg] = useState<string>('');
  const [signature, setSignature] = useState<string | null>(null);
  const [sigError, setSigError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rejectOpened, rejectModal] = useDisclosure(false);
  const [rejectReason, setRejectReason] = useState('');

  const form = useForm({
    initialValues: { parentFullName: '', passport: '', jshshir: '', parentAddress: '', parentPhone: '', agreed: false },
    validate: {
      parentFullName: (v) => (v.trim().length < 5 ? "F.I.Sh. to'liq kiriting" : null),
      passport: (v) => (/^[A-Z]{2}\s?\d{7}$/.test(v.trim().toUpperCase()) ? null : "Masalan: AB 1234567"),
      jshshir: (v) => (!v || v.replace(/\D/g, '').length === 14 ? null : '14 ta raqam bo\'lishi kerak'),
      parentAddress: (v) => (v.trim().length < 5 ? 'Yashash manzilini kiriting' : null),
      parentPhone: (v) => (v.replace(/\D/g, '').length >= 12 ? null : "Telefon raqamini to'liq kiriting"),
      agreed: (v) => (v ? null : 'Shartlarga rozilikni belgilang'),
    },
  });

  const loadInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/docs/${encodeURIComponent(token)}/info`);
      if (res.status === 404) return setLoad('notfound');
      // 410 — havola YOPIQ: bekor qilingan, muddati o'tgan yoki imzodan keyingi muddat
      // tugagan. Sababni server aniq yozadi, biz shu matnni ko'rsatamiz.
      if (res.status === 410) {
        setBlockMsg(await apiError(res));
        return setLoad('gone');
      }
      if (!res.ok) {
        setBlockMsg(await apiError(res));
        return setLoad('error');
      }
      const data = (await res.json()) as ContractInfo;
      setInfo(data);
      setLoad('ready');
      if (PENDING.includes(data.status) || PENDING.includes(data.statusRaw ?? data.status)) {
        form.setValues({
          parentFullName: data.prefill.parentFullName || '',
          passport: formatPassport(data.prefill.passport || ''),
          jshshir: (data.prefill.jshshir || '').replace(/\D/g, '').slice(0, 14),
          parentAddress: data.prefill.parentAddress || '',
          parentPhone: data.prefill.parentPhone || '',
          agreed: false,
        });
      }
    } catch {
      setBlockMsg("Internet aloqasi yo'q yoki server javob bermadi.");
      setLoad('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  /** Xato javobini ko'rsatadi va holat o'zgargan bo'lsa sahifani qayta yuklaydi. */
  const handleFail = async (res: Response, fallback?: string) => {
    notifications.show({ color: 'red', message: await apiError(res, fallback), autoClose: 7000 });
    // 404/409/410 — shartnoma holati serverda o'zgargan: ekranni yangilaymiz
    if ([404, 409, 410].includes(res.status)) await loadInfo();
  };

  const submit = async (v: typeof form.values) => {
    if (!signature) {
      setSigError('Imzo qo\'ying');
      return;
    }
    setSigError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/docs/${encodeURIComponent(token)}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parentFullName: v.parentFullName,
          passport: v.passport.toUpperCase(),
          jshshir: v.jshshir,
          parentAddress: v.parentAddress,
          parentPhone: v.parentPhone,
          signature,
          agreed: true,
        }),
      });
      if (!res.ok) {
        // 413 — imzo rasmi juda katta (server o'zbekcha javob beradi, proksi bo'lsa zaxira matn)
        await handleFail(res, res.status === 413 ? statusFallback(413) : undefined);
        return;
      }
      notifications.show({ color: 'green', message: 'Shartnoma tasdiqlandi' });
      await loadInfo();
    } catch {
      notifications.show({ color: 'red', message: 'Tarmoq xatosi. Keyinroq urinib ko\'ring.' });
    } finally {
      setSubmitting(false);
    }
  };

  const reject = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/docs/${encodeURIComponent(token)}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason }),
      });
      rejectModal.close();
      setRejectReason('');
      if (!res.ok) {
        await handleFail(res);
        return;
      }
      notifications.show({ color: 'yellow', message: 'Shartnoma rad etildi' });
      await loadInfo();
    } catch {
      notifications.show({ color: 'red', message: 'Tarmoq xatosi. Keyinroq urinib ko\'ring.' });
    } finally {
      setSubmitting(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/docs/${encodeURIComponent(token)}`);
      if (!res.ok) {
        // Bu endpoint xatoni HTML sahifa sifatida qaytaradi — JSON o'qib bo'lmaydi,
        // shuning uchun holatga mos o'zbekcha matn ko'rsatamiz.
        const msg =
          res.status === 410
            ? "Havola endi ishlamaydi — yuklab olish soni yoki muddati tugagan. Nusxani maktabdan yoki ota-ona ilovasidan oling."
            : statusFallback(res.status);
        notifications.show({ color: 'red', message: msg, autoClose: 7000 });
        await loadInfo(); // sabab ekranda ham ko'rinsin
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Shartnoma-${info?.summary.contractNumber ?? ''}.docx`.replace(/\s+/g, '');
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      await loadInfo();
    } catch {
      notifications.show({ color: 'red', message: 'Yuklashda xatolik' });
    } finally {
      setDownloading(false);
    }
  };

  // ── To'liq ekranli holatlar ────────────────────────────────
  if (load === 'loading') {
    return <Center mih="100vh"><Loader color="brand" /></Center>;
  }

  if (load !== 'ready' || !info) {
    // Bekor qilingan / muddati o'tgan (410) — server sababni aniq yozadi
    const cancelled = load === 'gone' && /bekor qilingan/i.test(blockMsg);
    const gone = load === 'gone';
    const title = load === 'notfound'
      ? 'Shartnoma topilmadi'
      : cancelled
        ? 'Shartnoma bekor qilingan'
        : gone
          ? 'Havola endi ishlamaydi'
          : 'Xatolik yuz berdi';
    const message = load === 'notfound'
      ? 'Bunday shartnoma havolasi mavjud emas. Havolani to\'liq ochganingizni tekshiring.'
      : blockMsg || "Ma'lumotni olishda xatolik. Iltimos, keyinroq urinib ko'ring.";
    const Ico = load === 'notfound' ? IconAlertTriangle : cancelled ? IconBan : gone ? IconClockOff : IconAlertTriangle;

    return (
      <ScreenCard
        color={gone ? 'orange' : 'red'}
        icon={<Ico size={34} />}
        title={title}
        message={message}
        hint={
          load === 'error'
            ? undefined
            : "Savollar bo'lsa yoki yangi havola kerak bo'lsa — maktab ma'muriyatiga murojaat qiling."
        }
        action={
          load === 'error' ? (
            <Button size="md" mt="md" leftSection={<IconRefresh size={18} />} onClick={() => { setLoad('loading'); void loadInfo(); }}>
              Qayta urinish
            </Button>
          ) : undefined
        }
      />
    );
  }

  const s = info.summary;
  const raw = info.statusRaw ?? info.status;
  const pending = PENDING.includes(info.status) || PENDING.includes(raw);
  const signed = info.status === 'SIGNED';
  const rejected = info.status === 'REJECTED';
  const cancelled = info.status === 'CANCELLED' || raw === 'CANCELLED';
  const viewed = raw === 'VIEWED';

  return (
    <Box mih="100vh" py="lg" px="xs" style={{ background: 'var(--mantine-color-gray-0)' }}>
      <Container size={640} px={0}>
        <Stack gap="md">
          {/* Sarlavha */}
          <Card radius="lg" p="lg">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <ThemeIcon size={44} radius="md" variant="light" color="brand" style={{ flexShrink: 0 }}>
                <IconFileText size={24} />
              </ThemeIcon>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fw={700} size="lg" lh={1.2}>Shartnoma № {s.contractNumber}</Text>
                <Text size="sm" c="dimmed">{info.schoolName}</Text>
              </Box>
              <Badge
                color={signed ? 'teal' : rejected ? 'red' : cancelled ? 'gray' : 'yellow'}
                variant="light"
                size="lg"
                style={{ flexShrink: 0 }}
              >
                {signed ? 'Tasdiqlangan' : rejected ? 'Rad etilgan' : cancelled ? 'Bekor qilingan' : 'Kutilmoqda'}
              </Badge>
            </Group>

            <Divider my="sm" />

            <Stack gap={6}>
              <InfoRow label="O'quvchi" value={s.studentFullName} />
              <InfoRow label="Shartnoma sanasi" value={dmy(s.date)} />
              <InfoRow label="Oylik to'lov" value={som(s.monthlyPayment)} />
              <InfoRow label="Davomiyligi" value={`${s.monthsCount} oy`} />
              <InfoRow label="Amal qilish muddati" value={`${dmy(s.fromDate)} — ${dmy(s.toDate)}`} />
            </Stack>
          </Card>

          {/* Shartnoma matni */}
          <Card radius="lg" p="lg">
            <Text fw={700} mb="xs">Shartnoma shartlari</Text>
            <ScrollArea.Autosize
              mah={320}
              type="auto"
              offsetScrollbars
              style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 10 }}
            >
              <Box p="sm">
                {info.sections.map((sec, i) => (
                  <Box key={i} mb="sm">
                    {sec.title && <Text fw={700} size="sm" ta="center" my={8}>{sec.title}</Text>}
                    {sec.lines.map((l, j) => (
                      <Text
                        key={j}
                        fz={13}
                        lh={1.55}
                        mb={4}
                        fw={l.bold ? 700 : 400}
                        ta={l.center ? 'center' : 'justify'}
                      >
                        {l.text}
                      </Text>
                    ))}
                  </Box>
                ))}
              </Box>
            </ScrollArea.Autosize>
          </Card>

          {/* Holatga qarab: forma / tasdiqlangan / rad etilgan / bekor qilingan */}
          {pending && !cancelled && (
            <Card radius="lg" p="lg">
              <Group gap="xs" mb="xs">
                <ThemeIcon size={28} radius="md" variant="light" color="brand"><IconWriting size={16} /></ThemeIcon>
                <Text fw={700}>Ma'lumotlaringizni kiriting</Text>
              </Group>
              <Text size="sm" c="dimmed" mb={info.expiresAt ? 4 : 'md'}>
                Quyidagi maydonlarni tekshirib chiqing va to'ldiring — ular shartnomaga kiritiladi.
              </Text>
              {/* Muddat ota-onaga OLDINDAN aytiladi: keyin 410 bilan to'satdan yopilib qolmasin */}
              {info.expiresAt && (
                <Text size="sm" c="dimmed" mb="md">
                  Havola <b>{dmy(info.expiresAt)}</b> gacha amal qiladi. Shundan keyin maktabdan yangi havola so'rashingiz kerak.
                </Text>
              )}
              {viewed && (
                <Text size="xs" c="dimmed" mb="md">
                  Siz shartnomani ochdingiz — maktab buni ko'radi. Imzolash uchun quyidagi formani to'ldiring.
                </Text>
              )}

              <form onSubmit={form.onSubmit(submit)} noValidate>
                <Stack gap="sm">
                  <TextInput
                    size="md"
                    label="F.I.Sh. (ota-ona yoki o'rnini bosuvchi shaxs)"
                    placeholder="Aliyev Vali Salimovich"
                    required
                    autoComplete="name"
                    {...form.getInputProps('parentFullName')}
                  />
                  <TextInput
                    size="md"
                    label="Pasport seriya va raqami"
                    placeholder="AB 1234567"
                    required
                    inputMode="text"
                    {...form.getInputProps('passport')}
                    onChange={(e) => form.setFieldValue('passport', formatPassport(e.currentTarget.value))}
                  />
                  <TextInput
                    size="md"
                    label="JSHSHIR (ixtiyoriy)"
                    placeholder="14 ta raqam"
                    inputMode="numeric"
                    {...form.getInputProps('jshshir')}
                    onChange={(e) => form.setFieldValue('jshshir', e.currentTarget.value.replace(/\D/g, '').slice(0, 14))}
                  />
                  <Textarea
                    size="md"
                    label="Yashash manzili"
                    placeholder="Namangan sh., ... ko'chasi, ...-uy"
                    autosize
                    minRows={2}
                    required
                    {...form.getInputProps('parentAddress')}
                  />
                  <PhoneInput size="md" label="Telefon raqami" required {...form.getInputProps('parentPhone')} />

                  <SignaturePad
                    onChange={(v) => { setSignature(v); if (v) setSigError(null); }}
                    error={sigError}
                    disabled={submitting}
                  />

                  <Checkbox
                    mt="xs"
                    size="md"
                    label="Shartnoma shartlari bilan tanishdim va roziman"
                    {...form.getInputProps('agreed', { type: 'checkbox' })}
                  />

                  <Button type="submit" size="md" mt="xs" fullWidth loading={submitting} leftSection={<IconCircleCheck size={18} />}>
                    Tasdiqlash va imzolash
                  </Button>
                  <Button
                    variant="subtle"
                    color="red"
                    size="md"
                    fullWidth
                    disabled={submitting}
                    leftSection={<IconCircleX size={16} />}
                    onClick={rejectModal.open}
                  >
                    Shartnomani rad etish
                  </Button>
                </Stack>
              </form>
            </Card>
          )}

          {signed && (
            <Card radius="lg" p="lg">
              <Stack align="center" gap="xs">
                <ThemeIcon size={64} radius="xl" variant="light" color="teal"><IconCircleCheck size={34} /></ThemeIcon>
                <Title order={4} ta="center">Shartnoma tasdiqlangan</Title>
                <Text size="sm" c="dimmed" ta="center">
                  {info.signerName ? `${info.signerName} tomonidan ` : ''}{dmyTime(info.signedAt)} da onlayn imzolangan.
                  Qayta imzolash talab qilinmaydi.
                </Text>
                {/* Imzo RASMI ochiq sahifada ko'rsatilmaydi (PII) — faqat qo'yilgani tasdiqlanadi. */}
                {info.hasSignature && (
                  <Badge color="teal" variant="light" size="lg" mt={4}>Imzo qo'yilgan</Badge>
                )}
                <Button
                  mt="md"
                  size="md"
                  fullWidth
                  leftSection={<IconDownload size={18} />}
                  onClick={download}
                  loading={downloading}
                  disabled={info.remainingUses <= 0}
                >
                  Shartnomani yuklab olish
                </Button>
                <Text size="xs" c="dimmed" ta="center">
                  {info.remainingUses > 0
                    ? `Bu havola orqali yana ${info.remainingUses} marta yuklab olishingiz mumkin. Nusxani hoziroq telefoningizga saqlab qo'ying — havola belgilangan muddatdan keyin yopiladi.`
                    : "Bu havola orqali yuklab olish tugadi. Shartnoma nusxasini ota-ona ilovasidan yoki maktab ma'muriyatidan oling."}
                </Text>
              </Stack>
            </Card>
          )}

          {rejected && (
            <Card radius="lg" p="lg">
              <Stack align="center" gap="xs">
                <ThemeIcon size={64} radius="xl" variant="light" color="red"><IconCircleX size={34} /></ThemeIcon>
                <Title order={4} ta="center">Shartnoma rad etilgan</Title>
                <Text size="sm" c="dimmed" ta="center">{dmyTime(info.rejectedAt)}</Text>
                {info.rejectReason && (
                  <Alert color="red" variant="light" w="100%" mt="xs" title="Rad etish sababi">
                    {info.rejectReason}
                  </Alert>
                )}
                <Text size="sm" c="dimmed" ta="center" mt="xs">
                  Maktab shartnomani tuzatib qayta yuborishi mumkin — yangi havolani kuting.
                  Savollar bo'lsa maktab ma'muriyatiga murojaat qiling.
                </Text>
              </Stack>
            </Card>
          )}

          {cancelled && (
            <Card radius="lg" p="lg">
              <Stack align="center" gap="xs">
                <ThemeIcon size={64} radius="xl" variant="light" color="gray"><IconBan size={34} /></ThemeIcon>
                <Title order={4} ta="center">Shartnoma bekor qilingan</Title>
                <Text size="sm" c="dimmed" ta="center">
                  Maktab bu shartnomani bekor qildi{info.cancelledAt ? ` (${dmyTime(info.cancelledAt)})` : ''}.
                  Uni imzolash mumkin emas.
                </Text>
                {info.cancelReason && (
                  <Alert color="gray" variant="light" w="100%" mt="xs" title="Bekor qilish sababi">
                    {info.cancelReason}
                  </Alert>
                )}
                <Text size="sm" c="dimmed" ta="center" mt="xs">
                  Yangi shartnoma kerak bo'lsa maktab ma'muriyatiga murojaat qiling.
                </Text>
              </Stack>
            </Card>
          )}

          <Text size="xs" c="dimmed" ta="center" fw={700} style={{ color: '#6C5CE7' }}>{info.schoolName}</Text>
        </Stack>
      </Container>

      <Modal opened={rejectOpened} onClose={rejectModal.close} title="Shartnomani rad etish" centered>
        <Stack>
          <Text size="sm" c="dimmed">
            Rad etilgandan so'ng bu havola orqali shartnomani imzolash mumkin bo'lmaydi.
            Sababni yozib qoldirsangiz, maktab siz bilan bog'lanadi va shartnomani tuzatib qayta yuborishi mumkin.
          </Text>
          <Textarea
            label="Sabab (ixtiyoriy)"
            placeholder="Masalan: ma'lumotlarda xatolik bor"
            autosize
            minRows={3}
            maxLength={500}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={rejectModal.close}>Bekor qilish</Button>
            <Button color="red" loading={submitting} onClick={reject}>Rad etish</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="sm" wrap="nowrap" align="flex-start">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>{label}</Text>
      <Text size="sm" fw={600} ta="right">{value}</Text>
    </Group>
  );
}

/** Bitta xabarli to'liq ekran (topilmadi / havola yopiq / xato) — telefonda o'qilishi oson. */
function ScreenCard({
  color, icon, title, message, hint, action,
}: {
  color: string;
  icon: ReactNode;
  title: string;
  message: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <Center mih="100vh" p="md" style={{ background: 'var(--mantine-color-gray-0)' }}>
      <Card p="xl" radius="lg" maw={420} w="100%" style={{ textAlign: 'center' }}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={64} radius="xl" variant="light" color={color}>{icon}</ThemeIcon>
          <Title order={4}>{title}</Title>
          <Text c="dimmed" size="sm">{message}</Text>
          {hint && <Text c="dimmed" size="xs">{hint}</Text>}
          {action}
        </Stack>
      </Card>
    </Center>
  );
}
